import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import authClient from '../services/authClient';
import { Wallet } from 'lucide-react';

const UserProfile = () => {
  const navigate = useNavigate();
  const { user, profile, logout, refreshProfile } = useAuth();
  const [updating, setUpdating] = useState({});
  const [updateMessage, setUpdateMessage] = useState('');
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    const loadProfile = async () => {
      if (user && !profile) {
        setLoading(true);
        try {
          await refreshProfile();
        } catch (error) {
          console.error("Failed to load profile", error);
        } finally {
          setLoading(false);
        }
      }
    };
    loadProfile();
  }, [user, profile, refreshProfile]);

  if (!user) {
    return <div style={{ padding: '40px', textAlign: 'center' }}>Please log in to view your profile.</div>;
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '4px solid #f3f3f3',
          borderTop: '4px solid #3b82f6',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!profile) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '60vh',
        gap: '20px',
        textAlign: 'center'
      }}>
        <div style={{ fontSize: '48px' }}>📝</div>
        <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#1f2937' }}>Complete Your Profile</h2>
        <p style={{ color: '#6b7280', maxWidth: '400px' }}>
          You haven't completed your astrology profile yet. We need your birth details to generate insights.
        </p>
        <button
          onClick={() => navigate('/signup')}
          style={{
            backgroundColor: '#3b82f6',
            color: 'white',
            padding: '12px 24px',
            borderRadius: '8px',
            border: 'none',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          Complete Setup
        </button>
        <button
          onClick={logout}
          style={{
            color: '#ef4444',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            textDecoration: 'underline'
          }}
        >
          Logout
        </button>
      </div>
    );
  }

  const handleUpdateData = async (dataType) => {
    try {
      setUpdating(prev => ({ ...prev, [dataType]: true }));
      setUpdateMessage('');

      // Call the backend to update specific astrology data
      const response = await authClient.makeRequest(`/auth/update-astrology-data`, {
        method: 'POST',
        body: JSON.stringify({ data_type: dataType })
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setUpdateMessage(`${dataType} data updated successfully!`);
          // Refresh the profile to get updated data
          await refreshProfile();
        } else {
          setUpdateMessage(`Failed to update ${dataType}: ${result.error}`);
        }
      } else {
        setUpdateMessage(`Failed to update ${dataType}: Server error`);
      }
    } catch (error) {
      console.error(`Error updating ${dataType}:`, error);
      setUpdateMessage(`Failed to update ${dataType}: ${error.message}`);
    } finally {
      setUpdating(prev => ({ ...prev, [dataType]: false }));
    }
  };

  const styles = {
    container: {
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '24px',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      marginBottom: '24px'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '24px',
      paddingBottom: '16px',
      borderBottom: '1px solid #e5e7eb'
    },
    title: {
      fontSize: '24px',
      fontWeight: 'bold',
      color: '#1f2937'
    },
    logoutButton: {
      backgroundColor: '#ef4444',
      color: 'white',
      padding: '8px 16px',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500'
    },
    profileGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: '24px'
    },
    section: {
      backgroundColor: '#f9fafb',
      borderRadius: '8px',
      padding: '20px'
    },
    sectionTitle: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#374151',
      marginBottom: '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    infoGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
      gap: '12px'
    },
    infoItem: {
      display: 'flex',
      flexDirection: 'column',
      gap: '4px'
    },
    infoLabel: {
      fontSize: '12px',
      fontWeight: '500',
      color: '#6b7280',
      textTransform: 'uppercase',
      letterSpacing: '0.05em'
    },
    infoValue: {
      fontSize: '16px',
      fontWeight: '600',
      color: '#1f2937'
    },
    numerologyGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
      gap: '12px'
    },
    numerologyCard: {
      backgroundColor: 'white',
      borderRadius: '6px',
      padding: '12px',
      textAlign: 'center',
      border: '1px solid #e5e7eb'
    },
    numerologyNumber: {
      fontSize: '24px',
      fontWeight: 'bold',
      color: '#3b82f6',
      marginBottom: '4px'
    },
    numerologyLabel: {
      fontSize: '12px',
      color: '#6b7280',
      fontWeight: '500'
    },
    astrologyData: {
      backgroundColor: 'white',
      borderRadius: '6px',
      padding: '12px',
      marginTop: '8px',
      border: '1px solid #e5e7eb'
    },
    dataItem: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: '4px 0',
      borderBottom: '1px solid #f3f4f6'
    },
    dataLabel: {
      fontSize: '14px',
      color: '#6b7280'
    },
    dataValue: {
      fontSize: '14px',
      fontWeight: '500',
      color: '#1f2937'
    },
    dataValueContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    updateButton: {
      backgroundColor: '#3b82f6',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      padding: '4px 8px',
      fontSize: '12px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'background-color 0.2s',
      display: 'flex',
      alignItems: 'center',
      gap: '4px'
    },
    updateButtonHover: {
      backgroundColor: '#2563eb'
    },
    updateButtonDisabled: {
      backgroundColor: '#9ca3af',
      cursor: 'not-allowed'
    },
    updateMessage: {
      backgroundColor: '#f0f9ff',
      color: '#0369a1',
      padding: '8px 12px',
      borderRadius: '6px',
      fontSize: '14px',
      fontWeight: '500',
      marginBottom: '16px',
      border: '1px solid #bae6fd'
    },
    updateAllContainer: {
      display: 'flex',
      justifyContent: 'center',
      marginBottom: '20px'
    },
    updateAllButton: {
      backgroundColor: '#10b981',
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      padding: '10px 20px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'background-color 0.2s',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    updateAllButtonHover: {
      backgroundColor: '#059669'
    },
    updateAllButtonDisabled: {
      backgroundColor: '#9ca3af',
      cursor: 'not-allowed'
    },
    statusBadge: {
      display: 'inline-block',
      padding: '4px 8px',
      borderRadius: '4px',
      fontSize: '12px',
      fontWeight: '500'
    },
    statusActive: {
      backgroundColor: '#dcfce7',
      color: '#166534'
    },
    statusVerified: {
      backgroundColor: '#dbeafe',
      color: '#1e40af'
    },
    importStatus: {
      backgroundColor: '#f0fdf4',
      color: '#166534',
      padding: '8px 12px',
      borderRadius: '6px',
      fontSize: '14px',
      fontWeight: '500',
      marginBottom: '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>🔮 Your Astro-Numerology Profile</h2>
        <button style={styles.logoutButton} onClick={logout}>
          Logout
        </button>
      </div>

      {/* Import Status */}
      <div style={styles.importStatus}>
        ✅ Complete astrology data imported and ready for analysis
      </div>

      {/* Wallet Section */}
      <div
        style={{
          ...styles.section,
          marginBottom: '24px',
          cursor: 'pointer',
          border: '1px solid #e5e7eb',
          transition: 'transform 0.2s, box-shadow 0.2s'
        }}
        onClick={() => navigate('/wallet')}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = 'none';
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              backgroundColor: '#eff6ff',
              padding: '10px',
              borderRadius: '10px',
              color: '#2563eb'
            }}>
              <Wallet size={24} />
            </div>
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937', margin: 0 }}>Wallet & Funds</h3>
              <p style={{ fontSize: '14px', color: '#6b7280', margin: '4px 0 0 0' }}>Manage deposits, withdrawals, and view balance</p>
            </div>
          </div>
          <button style={{
            ...styles.updateButton,
            padding: '8px 16px',
            fontSize: '14px'
          }}>
            Open Wallet →
          </button>
        </div>
      </div>

      <div style={styles.profileGrid}>
        {/* Basic Info */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>
            👤 Basic Information
          </h3>
          <div style={styles.infoGrid}>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Full Name</span>
              <span style={styles.infoValue}>{user.full_name}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Username</span>
              <span style={styles.infoValue}>@{user.username}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Email</span>
              <span style={styles.infoValue}>{user.email}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Status</span>
              <div>
                <span style={{
                  ...styles.statusBadge,
                  ...styles.statusActive
                }}>
                  {user.is_active ? 'Active' : 'Inactive'}
                </span>
                {user.is_verified && (
                  <span style={{
                    ...styles.statusBadge,
                    ...styles.statusVerified,
                    marginLeft: '8px'
                  }}>
                    Verified
                  </span>
                )}
              </div>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Member Since</span>
              <span style={styles.infoValue}>{formatDate(user.created_at)}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Last Login</span>
              <span style={styles.infoValue}>{formatDateTime(user.last_login)}</span>
            </div>
          </div>
        </div>

        {/* Birth Information */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>
            🌟 Birth Information
          </h3>
          <div style={styles.infoGrid}>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Birth Date</span>
              <span style={styles.infoValue}>{profile.profile.birth_date}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Birth Time</span>
              <span style={styles.infoValue}>{profile.profile.birth_time}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Birth Place</span>
              <span style={styles.infoValue}>{profile.profile.birth_place || 'N/A'}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Coordinates</span>
              <span style={styles.infoValue}>
                {profile.profile.latitude ?
                  `${profile.profile.latitude.toFixed(4)}, ${profile.profile.longitude.toFixed(4)}` :
                  'N/A'
                }
              </span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Timezone</span>
              <span style={styles.infoValue}>
                {profile.profile.timezone_offset ? `UTC${profile.profile.timezone_offset >= 0 ? '+' : ''}${profile.profile.timezone_offset}` : 'N/A'}
              </span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Last Analysis</span>
              <span style={styles.infoValue}>{formatDateTime(profile.profile.last_analysis)}</span>
            </div>
          </div>
        </div>

        {/* Numerology Numbers */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>
            🔢 Numerology Numbers
          </h3>
          <div style={styles.numerologyGrid}>
            <div style={styles.numerologyCard}>
              <div style={styles.numerologyNumber}>{profile.profile.life_path_number}</div>
              <div style={styles.numerologyLabel}>Life Path</div>
            </div>
            <div style={styles.numerologyCard}>
              <div style={styles.numerologyNumber}>{profile.profile.destiny_number}</div>
              <div style={styles.numerologyLabel}>Destiny</div>
            </div>
            <div style={styles.numerologyCard}>
              <div style={styles.numerologyNumber}>{profile.profile.soul_number}</div>
              <div style={styles.numerologyLabel}>Soul</div>
            </div>
            <div style={styles.numerologyCard}>
              <div style={styles.numerologyNumber}>{profile.profile.personality_number}</div>
              <div style={styles.numerologyLabel}>Personality</div>
            </div>
            <div style={styles.numerologyCard}>
              <div style={styles.numerologyNumber}>{profile.profile.maturity_number}</div>
              <div style={styles.numerologyLabel}>Maturity</div>
            </div>
            <div style={styles.numerologyCard}>
              <div style={styles.numerologyNumber}>{profile.profile.power_number}</div>
              <div style={styles.numerologyLabel}>Power</div>
            </div>
          </div>
        </div>

        {/* Current Periods */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>
            📅 Current Periods
          </h3>
          <div style={styles.infoGrid}>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Personal Year</span>
              <span style={styles.infoValue}>{profile.profile.personal_year}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Personal Month</span>
              <span style={styles.infoValue}>{profile.profile.personal_month}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Personal Day</span>
              <span style={styles.infoValue}>{profile.profile.personal_day}</span>
            </div>
          </div>
        </div>

        {/* Astrology Data Status */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>
            🔮 Astrology Data Status
          </h3>

          {/* Update Message */}
          {updateMessage && (
            <div style={styles.updateMessage}>
              {updateMessage}
            </div>
          )}

          {/* Update All Button */}
          <div style={styles.updateAllContainer}>
            <button
              style={styles.updateAllButton}
              onClick={() => handleUpdateData('all')}
              disabled={updating.all}
            >
              {updating.all ? '⏳' : '🔄'} Update All Missing Data
            </button>
          </div>

          <div style={styles.astrologyData}>
            <div style={styles.dataItem}>
              <span style={styles.dataLabel}>Birth Chart Data</span>
              <div style={styles.dataValueContainer}>
                <span style={styles.dataValue}>
                  {profile.profile.birth_chart_data ? '✅ Imported' : '❌ Not Available'}
                </span>
                {!profile.profile.birth_chart_data && (
                  <button
                    style={styles.updateButton}
                    onClick={() => handleUpdateData('birth_chart_data')}
                    disabled={updating.birth_chart_data}
                  >
                    {updating.birth_chart_data ? '⏳' : '🔄'} Update
                  </button>
                )}
              </div>
            </div>
            <div style={styles.dataItem}>
              <span style={styles.dataLabel}>Planetary Positions</span>
              <div style={styles.dataValueContainer}>
                <span style={styles.dataValue}>
                  {profile.profile.planetary_positions ? '✅ Imported' : '❌ Not Available'}
                </span>
                {!profile.profile.planetary_positions && (
                  <button
                    style={styles.updateButton}
                    onClick={() => handleUpdateData('planetary_positions')}
                    disabled={updating.planetary_positions}
                  >
                    {updating.planetary_positions ? '⏳' : '🔄'} Update
                  </button>
                )}
              </div>
            </div>
            <div style={styles.dataItem}>
              <span style={styles.dataLabel}>House Positions</span>
              <div style={styles.dataValueContainer}>
                <span style={styles.dataValue}>
                  {profile.profile.house_positions ? '✅ Imported' : '❌ Not Available'}
                </span>
                {!profile.profile.house_positions && (
                  <button
                    style={styles.updateButton}
                    onClick={() => handleUpdateData('house_positions')}
                    disabled={updating.house_positions}
                  >
                    {updating.house_positions ? '⏳' : '🔄'} Update
                  </button>
                )}
              </div>
            </div>
            <div style={styles.dataItem}>
              <span style={styles.dataLabel}>Dasha Data</span>
              <div style={styles.dataValueContainer}>
                <span style={styles.dataValue}>
                  {profile.profile.dasha_data ? '✅ Imported' : '❌ Not Available'}
                </span>
                {!profile.profile.dasha_data && (
                  <button
                    style={styles.updateButton}
                    onClick={() => handleUpdateData('dasha_data')}
                    disabled={updating.dasha_data}
                  >
                    {updating.dasha_data ? '⏳' : '🔄'} Update
                  </button>
                )}
              </div>
            </div>
            <div style={styles.dataItem}>
              <span style={styles.dataLabel}>Muhurta Data</span>
              <div style={styles.dataValueContainer}>
                <span style={styles.dataValue}>
                  {profile.profile.muhurta_data ? '✅ Imported' : '❌ Not Available'}
                </span>
                {!profile.profile.muhurta_data && (
                  <button
                    style={styles.updateButton}
                    onClick={() => handleUpdateData('muhurta_data')}
                    disabled={updating.muhurta_data}
                  >
                    {updating.muhurta_data ? '⏳' : '🔄'} Update
                  </button>
                )}
              </div>
            </div>
            <div style={styles.dataItem}>
              <span style={styles.dataLabel}>Numerology Report</span>
              <div style={styles.dataValueContainer}>
                <span style={styles.dataValue}>
                  {profile.profile.numerology_report ? '✅ Imported' : '❌ Not Available'}
                </span>
                {!profile.profile.numerology_report && (
                  <button
                    style={styles.updateButton}
                    onClick={() => handleUpdateData('numerology_report')}
                    disabled={updating.numerology_report}
                  >
                    {updating.numerology_report ? '⏳' : '🔄'} Update
                  </button>
                )}
              </div>
            </div>
            <div style={styles.dataItem}>
              <span style={styles.dataLabel}>Horoscope Data</span>
              <div style={styles.dataValueContainer}>
                <span style={styles.dataValue}>
                  {profile.profile.horoscope_data ? '✅ Imported' : '❌ Not Available'}
                </span>
                {!profile.profile.horoscope_data && (
                  <button
                    style={styles.updateButton}
                    onClick={() => handleUpdateData('horoscope_data')}
                    disabled={updating.horoscope_data}
                  >
                    {updating.horoscope_data ? '⏳' : '🔄'} Update
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
